import { useMutation } from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { unwrap } from '../api-error';

export type ResolveLinkPreviewResult =
  components['schemas']['ResolveLinkPreviewResponse'];
// The resolve endpoint returns `preview: LinkPreviewInput | null`; callers
// only ever store the non-null shape, so lift that out for state arrays.
export type LinkPreviewInput = NonNullable<
  components['schemas']['LinkPreviewInput']
>;

// Above the server's worst-case unfurl (≈10s page fetch + ≈8s image re-host),
// so this only trips on a genuinely stalled connection — never a slow-but-
// working page. It exists so a hung request can't leave a composer's
// send/publish gate stuck pending: every consumer degrades a rejected resolve
// to a minimal card, which clears the gate.
const RESOLVE_TIMEOUT_MS = 20_000;

export function useResolveLinkPreview() {
  return useMutation({
    mutationFn: async (input: {
      url: string;
    }): Promise<ResolveLinkPreviewResult> => {
      const client = getApiClient();
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), RESOLVE_TIMEOUT_MS);
      try {
        return unwrap(
          await client.POST('/api/v1/link-previews/resolve', {
            body: { url: input.url },
            signal: controller.signal,
          }),
        );
      } finally {
        clearTimeout(timer);
      }
    },
  });
}
